import { ResultsBox } from "../components/ResultsBox";
import { ResultsBoxEnglish } from "../components/ResultsBoxEnglish";
import { useEffect, useState } from "react";
import { AnalyseLayout } from "../components/layout/AnalyseLayout";
import { LoadingOverlay } from "../components/LoadingIndicator";
import { InputBox } from "../components/analyse/InputBox";
import { TextAnnotator } from "../components/analyse/TextAnnotator";
import { SampleTexts } from "../components/analyse/SampleTexts";
import { API } from "../helpers/api";
import { MAX_TEXT_WORDS, countWords } from "../helpers/constants";
import { useTranslation } from "../translations/LanguageContext";

export function Analyse() {
    const { t } = useTranslation();
    const [text, setText] = useState("");
    const [analysedText, setAnalysedText] = useState("")
    const [results, setResults] = useState(null);
    // Tracks which language's analysis produced the current `results`, so the
    // right ResultsBox variant (and TextAnnotator behaviour) can be chosen
    const [resultsLang, setResultsLang] = useState(null); // "cy" | "en"
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("input"); // "input" | "annotator" | "samples"
    const [errorMessage, setErrorMessage] = useState(null);

    // Test API is functioning - return error message to user otherwise
    function testAPI() {
        API.get("/health")
            .then(() => setErrorMessage(null))
            .catch(err => {
                console.error(err);
                setErrorMessage(t("common.errors.serviceUnavailable"));
            });
    }

    // Submit text for analysis from the API (Welsh version).
    // Accepts an optional override so callers (e.g. sample text selection)
    // can analyse text immediately without waiting on a state update.
    function analyseTextCy(textToAnalyse = text) {
        if (!textToAnalyse.trim()) return;
        if (countWords(textToAnalyse) > MAX_TEXT_WORDS) {
            setErrorMessage(t("inputBox.tooLong", { max: MAX_TEXT_WORDS }));
            return;
        }
        setLoading(true);
        setErrorMessage(null);
        API.post("/analyse", { text: textToAnalyse })
            .then(response => {
                setResults(response.data);
                setResultsLang("cy");
                setMode("annotator");
                setText(textToAnalyse);
                setAnalysedText(textToAnalyse);
            })
            .catch(err => {
                console.error(err);
                setErrorMessage(t("common.errors.analysisFailed"));
            })
            .finally(() => setLoading(false));
    }

    // Submit English text for analysis from the API.
    function analyseTextEn(textToAnalyse = text) {
        if (!textToAnalyse.trim()) return;
        if (countWords(textToAnalyse) > MAX_TEXT_WORDS) {
            setErrorMessage(t("inputBox.tooLong", { max: MAX_TEXT_WORDS }));
            return;
        }
        setLoading(true);
        setErrorMessage(null);
        API.post("/analyse_english", { text: textToAnalyse })
            .then(response => {
                setResults(response.data);
                setResultsLang("en");
                setMode("input");
                setText(textToAnalyse);
                setAnalysedText(textToAnalyse);
            })
            .catch(err => {
                console.error(err);
                setErrorMessage(t("common.errors.analysisFailed"));
            })
            .finally(() => setLoading(false));
    }

    // Open the sample texts browser
    function openSampleTexts() {
        setMode("samples");
    }

    // Load a sample text into the main textarea for review/editing
    function useSampleText(sampleText) {
        setText(sampleText);
        setMode("input");
    }

    // Load a sample text and analyse it immediately, routing to the
    // correct language's analysis function based on the sample's metadata.
    function analyseSampleText(sampleText, language) {
        if (language === "en") {
            analyseTextEn(sampleText);
        } else {
            analyseTextCy(sampleText);
        }
    }

    useEffect(() => {
        testAPI();
    }, []);

    const resultsOutdated =
        results &&
        text.trim() !== analysedText.trim();

    const isOverLimit = countWords(text) > MAX_TEXT_WORDS;

    return (
        <AnalyseLayout>
            {loading && <LoadingOverlay />}

            <section className="analyse-grid">
                <div className="analyse-left">
                    <div className="page-header">
                        <div className="page-header-left">
                            <h1 className="page-title">{t("analysePage.title")}</h1>
                            <h2 className="page-subtitle">{t("analysePage.subtitle")}</h2>
                        </div>
                    </div>

                    {/* Error Message */}
                    {errorMessage && (
                        <div className="error-banner" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    {/* Input box wrapper */}
                    <div className="input-column">
                        {mode === "input" && (
                            <>
                                <InputBox name="main" text={text} setText={setText} />

                                {results ? (
                                    <>
                                        <div className="action-row">
                                            {/* If Welsh mode, give option to inspect view to open annotator box*/}
                                            { resultsLang !== "en" ?
                                                <button className="action-btn" onClick={() => setMode("annotator")}>
                                                    <span className="action-arrow">←</span> {t("analysePage.inspectView")}
                                                </button>
                                                : ""
                                            }
                                            <button className="action-btn" onClick={() => analyseTextCy()} disabled={isOverLimit}>
                                                {resultsOutdated ? t("analysePage.reanalyseCy") : t("analysePage.analyseCy")}
                                                <span className="action-arrow">→</span>
                                            </button>
                                            <button className="action-btn" onClick={() => analyseTextEn()} disabled={isOverLimit}>
                                                {resultsOutdated ? t("analysePage.reanalyseEn") : t("analysePage.analyseEn")}
                                                <span className="action-arrow">→</span>
                                            </button>
                                        </div>
                                        <div className="action-row">
                                            <button className="action-btn" onClick={openSampleTexts}>
                                                {t("analysePage.sampleText")} <span className="action-arrow">→</span>
                                            </button>
                                        </div>
                                    </>

                                ) : (
                                    <>
                                        <div className="action-row">
                                            <button className="action-btn" onClick={() => analyseTextCy()} disabled={isOverLimit}>
                                                {t("analysePage.analyseCy")} <span className="action-arrow">→</span>
                                            </button>
                                            <button className="action-btn" onClick={() => analyseTextEn()} disabled={isOverLimit}>
                                                {t("analysePage.analyseEn")} <span className="action-arrow">→</span>
                                            </button>
                                        </div>
                                        <div className="action-row">
                                            <button className="action-btn" onClick={openSampleTexts}>
                                                {t("analysePage.sampleText")} <span className="action-arrow">→</span>
                                            </button>
                                        </div>
                                    </>

                                )}
                            </>
                        )}

                        {mode === "annotator" && (
                            // GAP: TextAnnotator expects `instances` (wordform/POS spans) to
                            // highlight in the text. English results currently have no
                            // `instances` key (readability_analysis_english doesn't return
                            // one), so annotation will likely no-op or error for English
                            // text until that function is extended to match the Welsh shape.
                            <TextAnnotator data={results} setMode={setMode}/>
                        )}

                        {mode === "samples" && (
                            <SampleTexts
                                onSelect={useSampleText}
                                onAnalyse={analyseSampleText}
                                onBack={() => setMode("input")}
                            />
                        )}
                    </div>
                </div>

                {/* Results box wrapper */}
                <div className="analyse-right">
                    {resultsLang === "en" ? (
                        <ResultsBoxEnglish data={results} isOutdated={resultsOutdated}/>
                    ) : (
                        <ResultsBox data={results} isOutdated={resultsOutdated}/>
                    )}
                </div>
            </section>
        </AnalyseLayout>
    );
}